import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { setStorageLang, getStorageLang } from '@stores/Language';
import { useLocation } from 'react-router-dom';

// Supported languages and default language
const supportedLanguages = ['en-US', 'tr-TR', 'az-AZ', 'ru-RU'] as const;
const defaultLanguage = 'tr-TR'; // Set a default language here

type LanguageContextType = {
    translations: Record<string, string>;
    setLang: (lang: typeof supportedLanguages[number]) => void;
    lang: typeof supportedLanguages[number];
};

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

type LanguageProviderProps = {
    children: ReactNode;
};

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
    // Ensure initial language falls back to default if undefined or unsupported
    const initialLang = (getStorageLang() as typeof supportedLanguages[number]) || defaultLanguage;
    const location = useLocation(); // React Router'dan gelen location objesi


    const [lang, setLang] = useState<typeof supportedLanguages[number]>(initialLang);
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const pathSegments = location.pathname.split('/'); // Path'i "/" ile böl
    const potentialLang = pathSegments[1]; // İlk segmenti alın (örneğin "az-AZ")
   
    // Load language JSON file and create a translations map
    const loadTranslations = async (language: typeof supportedLanguages[number]) => {
        const pathSegments = location.pathname.split('/'); // Path'i "/" ile böl
        const potentialLang = pathSegments[1]; // İlk segmenti alın (örneğin "az-AZ")
        const validLang = supportedLanguages.includes(potentialLang as typeof supportedLanguages[number])
            ? (potentialLang as typeof supportedLanguages[number])
            : null;


        let initialLang = ''

        if (validLang && supportedLanguages.includes(validLang)) {
            initialLang = validLang;
            setLang(validLang)
        } else {
            initialLang = language
        }
        try {
            const response = await import(`./locales/${initialLang}.json`);
            const translationsMap: Record<string, string> = {};
            response.default.forEach((item: { Header: string, Content: string }) => {
                translationsMap[item.Header] = item.Content;
            });
            setTranslations(createTranslationProxy(translationsMap));
        } catch (error) {
            console.error(`Dil dosyası yüklenemedi: ${initialLang}`, error);
            // Fallback to default language in case of error
            if (initialLang !== defaultLanguage) {
                loadTranslations(defaultLanguage);
            }
        }
    };

    // Load translations on initial load and when `lang` changes
    useEffect(() => {
        loadTranslations(lang);
    }, [lang]);

    useEffect(() => {
        const validLang = supportedLanguages.includes(potentialLang as typeof supportedLanguages[number])
        ? (potentialLang as typeof supportedLanguages[number])
        : null;
        if(lang !== potentialLang && (validLang && supportedLanguages.includes(validLang))){
            changeLanguage(validLang)
        }
    },[potentialLang])

    // Language change - only change if the new language is valid and different
    const changeLanguage = (newLang: typeof supportedLanguages[number]) => {
        if (newLang !== lang && supportedLanguages.includes(newLang)) {
            setLang(newLang);
            setStorageLang(newLang); // Save to localStorage
            loadTranslations(newLang); // Load new language translations
        }
    };

    function createTranslationProxy(translations: Record<string, string>): Record<string, string> {
        return new Proxy(translations, {
          get(target, prop: string) {
            return target[prop] || prop; // Eğer çeviri yoksa anahtarı döndür
          },
        });
      }

    return (
        <LanguageContext.Provider value={{ translations, setLang: changeLanguage, lang }}>
            {children}
        </LanguageContext.Provider>
    );
};

// Custom Hook for language usage
export const useLanguage = () => useContext(LanguageContext);
